import { Box } from '@uhg-abyss/web/ui/Box';
import { Brandmark } from '@uhg-abyss/web/ui/Brandmark';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { getClientPortalConfig } from '../../frontends/GuestExperience/utils';
import { useCurrentMember } from '../../hooks/useCurrentMember';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useGuestStore } from '../../store/useGuestStore';
import { GuestState } from '../../store/useGuestStore/guestStore';
import { ClientNames, Constants, UHC_EXCHANGE_ICON } from '../Constants';
import { ConstantsLagoon } from '../ConstantsLagoon';
import { PortalIdentifier } from '../Portal';
import { PSXIcon } from '../PSXIcon/PSXIcon';
import { useHarvardPilgrimEligibility } from './utils';

const handleLogoKeyDown = (e: React.KeyboardEvent, logoClick?: () => void) => {
  if ((e.key === 'Enter' || e.key === ' ') && logoClick) {
    e.preventDefault();
    try {
      logoClick();
    } catch {}
  }
};

interface GuestLogoProps {
  logoClick?: () => void;
  headerData?: {
    lob: string;
    coverage: string;
    signinUrl: string;
    brandmarkAffiliate: string;
    brandmarkVariant: string;
    languages: string;
  } | null;
}

export const GuestLogo: React.FC<GuestLogoProps> = ({
  headerData,
  logoClick: logoClick,
}) => {
  const hasCustomBrandmark =
    headerData?.brandmarkAffiliate && headerData?.brandmarkVariant;

  const uhcExchangeCustomBrandMark =
    headerData?.brandmarkAffiliate === UHC_EXCHANGE_ICON.AFFILIATE;
  const { t } = useTranslation();

  const clientName = sessionStorage.getItem(
    Constants.STORAGE_KEYS.SESSION.CLIENT_NAME
  );
  const clientPortalConfig = getClientPortalConfig(clientName);
  const isEmpire = clientName?.toLowerCase() === ClientNames.EMPIRE;
  const isGeha = clientPortalConfig.portalIdentifier === PortalIdentifier.GEHA;
  const { guestPlanSelectorData } = useGuestStore(
    useShallow((state: GuestState) => ({
      guestPlanSelectorData: state.guestPlanSelectorData,
    }))
  );
  const { reciprocityId: currentMemberReciprocityId } =
    useCurrentMember() || {};
  const reciprocityId =
    guestPlanSelectorData?.delsysCode || currentMemberReciprocityId;
  const [isHarvardPilgrimLogoEnabled] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_HARVARD_PILGRIM_LOGO,
  ]);
  const isEligibleForHarvardPilgrim =
    useHarvardPilgrimEligibility(reciprocityId);
  const isHarvardPilgrim =
    isHarvardPilgrimLogoEnabled && isEligibleForHarvardPilgrim;

  const getCustomIconConfig = () => {
    if (isEmpire) return { name: 'empire_logo', testId: 'guest-empire-logo' };
    if (isGeha) return { name: 'geha_brandingV2', testId: 'guest-geha-logo' };
    if (hasCustomBrandmark && uhcExchangeCustomBrandMark)
      return {
        name: UHC_EXCHANGE_ICON.AFFILIATE,
        testId: 'guest-exchange-custom-svg-icon',
      };
    if (isHarvardPilgrim)
      return { name: 'hphc_logo', testId: 'guest-hphc-logo' };
    return null;
  };
  const customIconConfig = getCustomIconConfig();

  return (
    <Box
      css={{
        'abyss-box-root': {
          display: 'flex',
          padding: 0,
          justifyContent: 'flex-start',
          alignItems: 'center',
          maxWidth: '242px',
          width: 'unset',
          height: '48px',
          backgroundColor: 'white',
        },
      }}
    >
      {customIconConfig ? (
        <PSXIcon
          aria-label={t('GUEST_LOGO_ACCESSIBILITY')}
          data-testid={customIconConfig.testId}
          icon={customIconConfig.name}
          onClick={logoClick}
          onKeyDown={(e) => handleLogoKeyDown(e, logoClick)}
          role="button"
          tabIndex={0}
        />
      ) : hasCustomBrandmark ? (
        <Brandmark
          affiliate={headerData.brandmarkAffiliate}
          aria-label={t('GUEST_LOGO_ACCESSIBILITY')}
          brand="uhc"
          color="blue"
          css={{
            'abyss-brandmark': {
              height: '48px',
              width: headerData?.brandmarkAffiliate.includes('community')
                ? '242px'
                : 'auto',
              display: 'block',
            },
          }}
          data-testid="guest-logo-brandmark"
          onClick={logoClick}
          onKeyDown={(e) => handleLogoKeyDown(e, logoClick)}
          role="button"
          size="100%"
          tabIndex={0}
          variant={headerData.brandmarkVariant}
        />
      ) : (
        <Brandmark
          affiliate="uhc"
          aria-label={t('GUEST_LOGO_ACCESSIBILITY')}
          brand="uhc"
          color="blue"
          css={{
            'abyss-brandmark': {
              height: '48px',
              width: 'auto',
              display: 'block',
            },
          }}
          data-testid="guest-logo-brandmark"
          onClick={logoClick}
          onKeyDown={(e) => handleLogoKeyDown(e, logoClick)}
          role="button"
          size="100%"
          tabIndex={0}
          variant="lockup"
        />
      )}
    </Box>
  );
};
